/**
 * The HTTP contract shared by client and server.
 *
 * Request payloads are validated with these schemas on the server and reused by
 * the client for form validation, so the two can never drift apart. Responses
 * are built by explicit mapper functions on the server (see `dto.ts` there),
 * which keeps fields such as the password hash structurally unable to leak.
 */

import { z } from 'zod';
import { MAX_SLOT, MIN_SLOT, SLOTS_PER_DAY } from './slots.js';
import { PALETTE_SIZE } from './palette.js';

/** Largest window a single assignment query may request: 400 days. */
export const MAX_QUERY_WINDOW_SLOTS = 400 * SLOTS_PER_DAY;

export const ERROR_CODES = {
  VALIDATION: 'VALIDATION',
  UNAUTHORIZED: 'UNAUTHORIZED',
  FORBIDDEN: 'FORBIDDEN',
  NOT_FOUND: 'NOT_FOUND',
  /** The requested range collides with an existing assignment. */
  CONFLICT_OVERLAP: 'CONFLICT_OVERLAP',
  /** The assignment changed or was deleted since it was loaded. */
  CONFLICT_STALE: 'CONFLICT_STALE',
  PERSON_HAS_ASSIGNMENTS: 'PERSON_HAS_ASSIGNMENTS',
  NAME_TAKEN: 'NAME_TAKEN',
  RATE_LIMITED: 'RATE_LIMITED',
  INTERNAL: 'INTERNAL',
} as const;

export type ErrorCode = (typeof ERROR_CODES)[keyof typeof ERROR_CODES];

export interface ApiErrorBody {
  error: {
    code: ErrorCode;
    message: string;
    details?: unknown;
  };
}

export const API_PREFIX = '/api';

export const API_PATHS = {
  login: '/api/auth/login',
  logout: '/api/auth/logout',
  me: '/api/auth/me',
  persons: '/api/persons',
  person: (id: number) => `/api/persons/${id}`,
  assignments: '/api/assignments',
  assignment: (id: number) => `/api/assignments/${id}`,
  upcoming: '/api/assignments/upcoming',
  clearRange: '/api/assignments/clear-range',
  changelog: '/api/changelog',
  events: '/api/events',
  health: '/api/health',
} as const;

// ---------------------------------------------------------------------------
// Primitives
// ---------------------------------------------------------------------------

const slotSchema = z.number().int().min(MIN_SLOT).max(MAX_SLOT);
const idSchema = z.number().int().positive();
const nameSchema = z.string().trim().min(1, 'Name darf nicht leer sein').max(60);
const passwordSchema = z.string().min(4, 'Passwort ist zu kurz').max(200);
const colorIndexSchema = z.number().int().min(1).max(PALETTE_SIZE);

export const idParamSchema = z.object({ id: z.coerce.number().int().positive() });

// ---------------------------------------------------------------------------
// Auth
// ---------------------------------------------------------------------------

export const loginBodySchema = z.object({
  name: nameSchema,
  password: z.string().min(1, 'Passwort darf nicht leer sein').max(200),
});
export type LoginBody = z.infer<typeof loginBodySchema>;

// ---------------------------------------------------------------------------
// Persons
// ---------------------------------------------------------------------------

const personFieldsSchema = z.object({
  name: nameSchema,
  colorIndex: colorIndexSchema,
  hasAccount: z.boolean(),
  isAdmin: z.boolean(),
  /**
   * A Betreuungsperson. Required rather than defaulted: defaulting it would
   * grant assignability to a caller that simply misspelt the field.
   */
  isCaregiver: z.boolean(),
  /** Required when creating a person with an account; optional on update. */
  password: passwordSchema.optional(),
});

export const createPersonBodySchema = personFieldsSchema
  .refine((value) => !value.hasAccount || value.password !== undefined, {
    message: 'Für einen Account wird ein Passwort benötigt',
    path: ['password'],
  })
  .refine((value) => !value.isAdmin || value.hasAccount, {
    message: 'Admin-Berechtigung setzt einen Account voraus',
    path: ['isAdmin'],
  })
  // A person who is neither assignable nor able to sign in would be a row
  // nothing can reach.
  .refine((value) => value.isCaregiver || value.hasAccount, {
    message: 'Nur Ansicht setzt einen Account voraus',
    path: ['hasAccount'],
  })
  .refine((value) => !value.isAdmin || value.isCaregiver, {
    message: 'Nur Ansicht und Admin-Berechtigung schließen sich aus',
    path: ['isAdmin'],
  });
export type CreatePersonBody = z.infer<typeof createPersonBodySchema>;

/**
 * Built from the *unrefined* base, so none of the cross-field rules above apply
 * here — a PATCH carries an arbitrary subset and cannot be judged in isolation.
 * `PersonService.update` is authoritative for those rules; it sees the stored row
 * as well as the change. Adding the refines to this schema is not an option
 * either way: `.partial()` on a refined schema throws.
 */
export const updatePersonBodySchema = personFieldsSchema
  .partial()
  .refine((value) => Object.keys(value).length > 0, { message: 'Keine Änderungen übergeben' });
export type UpdatePersonBody = z.infer<typeof updatePersonBodySchema>;

// ---------------------------------------------------------------------------
// Assignments
// ---------------------------------------------------------------------------

export const assignmentRangeQuerySchema = z
  .object({
    from: z.coerce.number().int().min(MIN_SLOT).max(MAX_SLOT),
    to: z.coerce.number().int().min(MIN_SLOT).max(MAX_SLOT),
  })
  .refine((value) => value.to > value.from, {
    message: 'to muss größer als from sein',
    path: ['to'],
  })
  .refine((value) => value.to - value.from <= MAX_QUERY_WINDOW_SLOTS, {
    message: 'Zeitfenster ist zu groß',
    path: ['to'],
  });
export type AssignmentRangeQuery = z.infer<typeof assignmentRangeQuerySchema>;

export const upcomingQuerySchema = z.object({
  /** Exclusive lower bound for paging: only assignments starting later. */
  cursor: z.coerce.number().int().min(MIN_SLOT).max(MAX_SLOT).optional(),
  limit: z.coerce.number().int().min(1).max(200).default(30),
});
export type UpcomingQuery = z.infer<typeof upcomingQuerySchema>;

export const createAssignmentBodySchema = z
  .object({
    personId: idSchema,
    startSlot: slotSchema,
    endSlot: slotSchema,
  })
  .refine((value) => value.endSlot > value.startSlot, {
    message: 'Ende muss nach dem Beginn liegen',
    path: ['endSlot'],
  });
export type CreateAssignmentBody = z.infer<typeof createAssignmentBodySchema>;

export const updateAssignmentBodySchema = z
  .object({
    personId: idSchema,
    startSlot: slotSchema,
    endSlot: slotSchema,
    /** `updatedAt` of the assignment as it was loaded — optimistic concurrency. */
    expectedUpdatedAt: z.number().int().nonnegative(),
  })
  .refine((value) => value.endSlot > value.startSlot, {
    message: 'Ende muss nach dem Beginn liegen',
    path: ['endSlot'],
  });
export type UpdateAssignmentBody = z.infer<typeof updateAssignmentBodySchema>;

export const clearRangeBodySchema = z
  .object({
    startSlot: slotSchema,
    endSlot: slotSchema,
  })
  .refine((value) => value.endSlot > value.startSlot, {
    message: 'Ende muss nach dem Beginn liegen',
    path: ['endSlot'],
  });
export type ClearRangeBody = z.infer<typeof clearRangeBodySchema>;

// ---------------------------------------------------------------------------
// Changelog
// ---------------------------------------------------------------------------

export const changelogQuerySchema = z.object({
  /** Exclusive upper bound: return entries with a smaller id (older entries). */
  cursor: z.coerce.number().int().positive().optional(),
  limit: z.coerce.number().int().min(1).max(100).default(30),
});
export type ChangelogQuery = z.infer<typeof changelogQuerySchema>;

// ---------------------------------------------------------------------------
// Server-sent events
// ---------------------------------------------------------------------------

export const SSE_EVENT_CHANGE = 'change';
/** Sent on connect; carries the newest known event id. */
export const SSE_EVENT_HELLO = 'hello';
/** Sent when a reconnect cannot be replayed — the client must refetch everything. */
export const SSE_EVENT_RESET = 'reset';

export interface SseHelloPayload {
  lastEventId: number;
}
